import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { localWallTimeUtc } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useOrgClock } from '../../lib/useOrgClock';
import { Field, field } from '../create/Field';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconTeam } from '../icons';
import { quoteWindowInput } from '../money/QuotePlanningFields';
import { useOnline } from '../ui/useConnection';
import { CrewAvailabilityTimeline } from './CrewAvailabilityTimeline';

export type CrewCandidate = { userId: string; startsAt: Date; endsAt: Date };

export function CrewAvailabilityPanel({
  initialUserId,
  initialDurationMinutes,
  initialStartAt,
  initialEndAt,
  onApply,
}: {
  initialUserId?: string;
  initialDurationMinutes?: number;
  initialStartAt?: string;
  initialEndAt?: string;
  onApply?: (candidate: CrewCandidate) => void;
}) {
  const trpc = useTRPC();
  const clock = useOrgClock();
  const online = useOnline();
  const [when, setWhen] = useState('');
  const [duration, setDuration] = useState(String(initialDurationMinutes ?? 60));
  useEffect(() => {
    if (!initialStartAt || !clock.tz) return;
    setWhen(quoteWindowInput(initialStartAt, clock.tz));
    if (initialEndAt)
      setDuration(
        String(
          (new Date(initialEndAt).getTime() - new Date(initialStartAt).getTime()) /
            60_000,
        ),
      );
  }, [initialStartAt, initialEndAt, clock.tz]);
  const [allCrew, setAllCrew] = useState(!initialUserId);
  const [requested, setRequested] = useState<{
    startsAt: Date;
    endsAt: Date;
    userIds?: string[];
  } | null>(null);
  const q = useQuery({
    ...trpc.scheduleAvailability.availability.queryOptions(
      requested ?? { startsAt: new Date(0), endsAt: new Date(1) },
    ),
    enabled: requested !== null,
  });
  const state = shownState(q, q.data);
  const check = () => {
    if (!when || !clock.tz || !Number.isFinite(Number(duration)) || Number(duration) <= 0)
      return;
    const [day, time] = when.split('T');
    const [hour, minute] = time!.split(':').map(Number);
    const startsAt = localWallTimeUtc(day!, hour! * 60 + minute!, clock.tz);
    setRequested({
      startsAt,
      endsAt: new Date(startsAt.getTime() + Number(duration) * 60_000),
      ...(!allCrew && initialUserId ? { userIds: [initialUserId] } : {}),
    });
  };
  return (
    <div className="space-y-4">
      <Notice tone="neutral">
        Availability is informational while quoting. This check creates no calendar hold.
        Booking is checked again when a visit is scheduled.
      </Notice>
      {clock.state !== 'ready' ? (
        <UnresolvedState
          state={clock.state}
          icon={IconTeam}
          what="the shop's timezone"
          onRetry={clock.refetch}
        />
      ) : (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            check();
          }}
        >
          <Field label={`Requested start · ${clock.tz}`}>
            <input
              className={field}
              type="datetime-local"
              value={when}
              required
              onChange={(event) => {
                setWhen(event.target.value);
                setRequested(null);
              }}
            />
          </Field>
          <Field label="Duration in minutes">
            <input
              className={field}
              type="number"
              min={1}
              max={44640}
              step={1}
              value={duration}
              required
              onChange={(event) => {
                setDuration(event.target.value);
                setRequested(null);
              }}
            />
          </Field>
          {initialUserId ? (
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={allCrew}
                onChange={(event) => {
                  setAllCrew(event.target.checked);
                  setRequested(null);
                }}
              />{' '}
              Show all crews
            </label>
          ) : null}
          <Button type="submit" disabled={!online || q.isFetching}>
            Check availability
          </Button>
        </form>
      )}
      {requested ? (
        <>
          <StaleNotice
            show={refreshFailed(q, q.data) || q.isPaused}
            onRetry={() => void q.refetch()}
          />
          {state !== 'ready' ? (
            <UnresolvedState
              state={state}
              icon={IconTeam}
              what="crew availability"
              error={q.error}
              onRetry={() => void q.refetch()}
            />
          ) : q.data ? (
            <>
              <p className="text-xs text-muted-foreground">
                Working-hours timezone: {q.data.timezone} · {q.data.travelBufferMinutes}{' '}
                minute travel buffer between adjacent visits. Requested dates use the shop
                timezone shown above.
              </p>
              {!q.data.configured ? (
                <Notice tone="warn">
                  Working hours are not fully configured. Unknown availability does not
                  mean a crew is free.
                </Notice>
              ) : null}
              <ul className="divide-y divide-border">
                {q.data.crew.map((crew) => (
                  <li key={crew.userId} className="space-y-2 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold">{crew.name}</p>
                      <StatusBadge
                        tone={
                          crew.state === 'available'
                            ? 'success'
                            : crew.state === 'conflict'
                              ? 'warn'
                              : 'neutral'
                        }
                      >
                        {crew.state === 'available'
                          ? 'Available'
                          : crew.state === 'conflict'
                            ? 'Conflict'
                            : 'Unknown'}
                      </StatusBadge>
                    </div>
                    {crew.conflicts.length ? (
                      <p className="text-xs text-muted-foreground">
                        {crew.conflicts
                          .map((conflict) => conflict.kind.replaceAll('_', ' '))
                          .join(' · ')}
                      </p>
                    ) : null}
                    {onApply ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={
                          !online ||
                          q.isFetching ||
                          q.isError ||
                          q.isPaused ||
                          crew.state !== 'available'
                        }
                        onClick={() => {
                          if (
                            requested &&
                            online &&
                            !q.isFetching &&
                            !q.isError &&
                            !q.isPaused &&
                            crew.state === 'available'
                          )
                            onApply({
                              userId: crew.userId,
                              startsAt: requested.startsAt,
                              endsAt: requested.endsAt,
                            });
                        }}
                      >
                        Use this crew and requested window
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
              {q.data.crew.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No eligible crew members match this check.
                </p>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
      <CrewAvailabilityTimeline initialDay={when.slice(0, 10) || undefined} />
    </div>
  );
}
